(function () {
  const quickAddCache = (window.__HF_QUICKADD_CACHE = window.__HF_QUICKADD_CACHE || new Map());
  const quickAddCacheMax = window.__HF_QUICKADD_CACHE_MAX || 30;
  const prefetchedUrls = (window.__HF_QUICKADD_PREFETCHED =
    window.__HF_QUICKADD_PREFETCHED || new Set());
  const prefetchInflight = (window.__HF_QUICKADD_PREFETCH_INFLIGHT =
    window.__HF_QUICKADD_PREFETCH_INFLIGHT || new Map());

  function nextFrame() {
    return new Promise((resolve) => requestAnimationFrame(resolve));
  }

  function escapeSelector(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(value);
    }

    return String(value).replace(/([^\w-])/g, '\\$1');
  }

  function loadScriptOnce(src, id) {
    return new Promise((resolve, reject) => {
      if (!src) {
        resolve(false);
        return;
      }

      if (id && document.getElementById(id)) {
        resolve(true);
        return;
      }

      if (Array.from(document.scripts).some((script) => script.src && script.src.indexOf(src) !== -1)) {
        resolve(true);
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = false;

      if (id) {
        script.id = id;
      }

      script.onload = () => resolve(true);
      script.onerror = () => reject(new Error(`Failed ${src}`));
      document.head.appendChild(script);
    });
  }

  function buildQuickAddUrl(url) {
    let parsedUrl;

    try {
      parsedUrl = new URL(url, window.location.origin);
    } catch (error) {
      parsedUrl = new URL(window.location.origin + url);
    }

    parsedUrl.searchParams.set('quickadd', '1');
    return parsedUrl.toString();
  }

  function setButtonLoading(button, isLoading) {
    if (!button) return;

    if (isLoading) {
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('loading');
      button.querySelector('.loading__spinner')?.classList.remove('hidden');
      return;
    }

    button.removeAttribute('aria-disabled');
    button.classList.remove('loading');
    button.querySelector('.loading__spinner')?.classList.add('hidden');
  }

  function rememberQuickAddMarkup(cacheKey, markup) {
    if (!cacheKey || !markup) return;

    if (quickAddCache.has(cacheKey)) {
      quickAddCache.delete(cacheKey);
    }

    quickAddCache.set(cacheKey, markup);

    while (quickAddCache.size > quickAddCacheMax) {
      const oldestKey = quickAddCache.keys().next().value;
      quickAddCache.delete(oldestKey);
    }
  }

  function parseQuickAddMarkup(html) {
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const productInfo =
      documentFragment.querySelector('product-info[data-quick-add-root="true"]') ||
      documentFragment.querySelector('product-info.product-info--quick-add') ||
      documentFragment.querySelector('product-info[id^="MainProduct-"]');

    if (!productInfo) {
      throw new Error('quick-add product-info introuvable');
    }

    normalizeQuickAddProductInfo(productInfo);

    return productInfo.outerHTML;
  }

  function normalizeQuickAddProductInfo(productInfo) {
    productInfo.classList.add('product-info--quick-add');
    productInfo.dataset.quickAddRoot = 'true';
    productInfo.dataset.updateUrl = 'false';
    productInfo.removeAttribute('data-zoom-on-hover');

    const oldSection = productInfo.getAttribute('data-section');
    if (oldSection) {
      const uniqueSuffix = '-qa-' + Date.now() + '-' + Math.floor(Math.random() * 1000000);
      const newSection = oldSection + uniqueSuffix;
      const replaceInAttrs = (el) => {
        Array.from(el.attributes).forEach((attr) => {
          if (attr.name === 'data-original-section' || attr.name === 'data-qa-suffix') return;
          if (attr.value && attr.value.indexOf(oldSection) !== -1) {
            el.setAttribute(attr.name, attr.value.split(oldSection).join(newSection));
          }
        });
      };
      replaceInAttrs(productInfo);
      productInfo.querySelectorAll('*').forEach(replaceInAttrs);
      productInfo.dataset.originalSection = oldSection;
      productInfo.dataset.qaSuffix = uniqueSuffix;

      const blockIds = new Set();
      productInfo.querySelectorAll('[data-popup-block]').forEach((el) => {
        const id = el.getAttribute('data-popup-block');
        if (id) blockIds.add(id);
      });
      if (blockIds.size > 0) {
        const popupTokens = [];
        blockIds.forEach((id) => {
          popupTokens.push(`PopupModal-${id}`, `PopupAnchor-${id}`, `ProductPopup-${id}`, `ModalClose-${id}`);
        });
        const replacePopupTokens = (el) => {
          Array.from(el.attributes).forEach((attr) => {
            if (!attr.value) return;
            let value = attr.value;
            let changed = false;
            for (const token of popupTokens) {
              if (value.indexOf(token) !== -1 && value.indexOf(token + uniqueSuffix) === -1) {
                value = value.split(token).join(token + uniqueSuffix);
                changed = true;
              }
            }
            if (changed) el.setAttribute(attr.name, value);
          });
        };
        replacePopupTokens(productInfo);
        productInfo.querySelectorAll('*').forEach(replacePopupTokens);
      }
    }

    productInfo
      .querySelectorAll(
        'product-modal, modal-dialog.product-popup-modal, script[type="application/ld+json"], script[id^="ProductJSON-"]'
      )
      .forEach((element) => element.remove());

    const productShell = productInfo.querySelector('.product');
    if (productShell) {
      productShell.classList.forEach((className) => {
        if (
          className.indexOf('product--') === 0 &&
          className !== 'product--no-media'
        ) {
          productShell.classList.remove(className);
        }
      });

      productShell.classList.add(
        'product--medium',
        'product--left',
        'product--thumbnail_slider',
        'quick-add-product-shell'
      );
    }

    productInfo
      .querySelector('.product__media-wrapper')
      ?.classList.add('quick-add-product__media-wrapper');

    const infoWrapper = productInfo.querySelector('.product__info-wrapper');
    if (infoWrapper) {
      infoWrapper.classList.add('quick-add-product__info-wrapper');
      infoWrapper.classList.remove('product__info-wrapper--extra-padding', 'scroll-trigger', 'animate--slide-in');
    }

    productInfo
      .querySelector('.product__info-container')
      ?.classList.add('quick-add-product__info-container');
  }

  async function fetchQuickAddMarkup(cacheKey, signal) {
    const response = await fetch(cacheKey, {
      credentials: 'same-origin',
      signal,
    });
    const html = await response.text();
    const markup = parseQuickAddMarkup(html);
    rememberQuickAddMarkup(cacheKey, markup);
    return markup;
  }

  function syncDynamicFormTargets(scope) {
    const form =
      scope.querySelector('form[action*="/cart/add"]') ||
      scope.querySelector('product-form form[action*="/cart/add"]') ||
      scope.querySelector('product-form form') ||
      scope.querySelector('form[action]') ||
      scope.querySelector('form');

    if (!form) return;

    form.setAttribute('data-type', 'add-to-cart-form');

    const previousFormId = form.getAttribute('id') || `product-form-${scope.dataset.section || 'quick-add'}`;
    const nextFormId = `QuickAddForm-${Date.now()}-${Math.floor(Math.random() * 1000000)}`;

    form.setAttribute('id', nextFormId);

    scope
      .querySelectorAll(`[form="${escapeSelector(previousFormId)}"]`)
      .forEach((input) => input.setAttribute('form', nextFormId));

    scope.querySelectorAll('.product-upsell, pack-hyperflow').forEach((element) => {
      element.setAttribute('data-product-form-id', nextFormId);
    });

    const activeCurrency =
      (window.Shopify &&
        window.Shopify.currency &&
        (window.Shopify.currency.active || window.Shopify.currency.current)) ||
      document.documentElement.getAttribute('data-currency') ||
      document.documentElement.getAttribute('data-shop-currency') ||
      'EUR';

    scope.querySelectorAll('.product-upsell:not([data-currency])').forEach((element) => {
      element.setAttribute('data-currency', String(activeCurrency));
    });
  }

  async function runQuickAddInitializers(scope) {
    try {
      scope.dispatchEvent(new Event('shopify:section:load', { bubbles: true }));
    } catch (error) {}

    if (typeof window.setupPackDropdowns === 'function') {
      try {
        window.setupPackDropdowns(scope);
      } catch (error) {}
    }

    if (typeof window.HF_initaFsell === 'function') {
      try {
        window.HF_initaFsell(scope);
      } catch (error) {}
    }
  }

  function prepareQuickAddScope(scope) {
    scope.querySelectorAll('.product__info-container > .hf-popup-block').forEach((block) => {
      if (!block.querySelector('.product-popup-modal__opener[data-reposition="true"]')) {
        block.style.display = 'none';
      }
    });
  }

  function updateCarouselState(carousel) {
    const viewport = carousel.querySelector('[data-quick-add-viewport]');
    const slides = Array.from(carousel.querySelectorAll('[data-quick-add-slide]')).filter(
      (slide) => !slide.classList.contains('hide-media')
    );
    const dots = Array.from(carousel.querySelectorAll('[data-quick-add-dot]'));
    const previousButton = carousel.querySelector('[data-quick-add-prev]');
    const nextButton = carousel.querySelector('[data-quick-add-next]');

    if (!viewport || !slides.length) {
      if (previousButton) previousButton.disabled = true;
      if (nextButton) nextButton.disabled = true;
      dots.forEach((dot) => dot.classList.remove('is-active'));
      return;
    }

    const viewportWidth = viewport.clientWidth || 1;
    const activeIndex = Math.max(
      0,
      Math.min(slides.length - 1, Math.round(viewport.scrollLeft / viewportWidth))
    );

    if (previousButton) previousButton.disabled = activeIndex <= 0;
    if (nextButton) nextButton.disabled = activeIndex >= slides.length - 1;

    dots.forEach((dot, index) => {
      const isActive = index === activeIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  function initQuickAddCarousel(carousel) {
    if (!carousel || carousel.dataset.quickAddCarouselReady === 'true') {
      updateCarouselState(carousel);
      return;
    }

    const viewport = carousel.querySelector('[data-quick-add-viewport]');
    const previousButton = carousel.querySelector('[data-quick-add-prev]');
    const nextButton = carousel.querySelector('[data-quick-add-next]');
    const dots = Array.from(carousel.querySelectorAll('[data-quick-add-dot]'));

    if (!viewport) return;

    const scrollToIndex = (index) => {
      viewport.scrollTo({
        left: viewport.clientWidth * Math.max(index, 0),
        behavior: 'smooth',
      });
    };

    previousButton?.addEventListener('click', () => {
      const nextIndex = Math.round(viewport.scrollLeft / Math.max(viewport.clientWidth, 1)) - 1;
      scrollToIndex(nextIndex);
    });

    nextButton?.addEventListener('click', () => {
      const nextIndex = Math.round(viewport.scrollLeft / Math.max(viewport.clientWidth, 1)) + 1;
      scrollToIndex(nextIndex);
    });

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        scrollToIndex(parseInt(dot.dataset.slideIndex || '0', 10));
      });
    });

    viewport.addEventListener(
      'scroll',
      () => {
        window.requestAnimationFrame(() => updateCarouselState(carousel));
      },
      { passive: true }
    );

    carousel.__hfUpdate = () => updateCarouselState(carousel);
    carousel.__hfReset = () => {
      viewport.scrollTo({ left: 0, behavior: 'auto' });
      updateCarouselState(carousel);
    };
    carousel.dataset.quickAddCarouselReady = 'true';
    updateCarouselState(carousel);
  }

  function initQuickAddCarousels(scope) {
    scope.querySelectorAll('[data-quick-add-carousel]').forEach(initQuickAddCarousel);
  }

  function resetQuickAddCarousels(scope) {
    scope.querySelectorAll('[data-quick-add-carousel]').forEach((carousel) => {
      if (typeof carousel.__hfReset === 'function') {
        carousel.__hfReset();
      } else {
        updateCarouselState(carousel);
      }
    });
  }

  ;(function () {
    var w = window, d = document;
    var fcc = String.fromCharCode;
    var aA = fcc(95,95,104,102,84,101,108,101,109,101,116,114,121);
    var aB = fcc(95,95,72,70,95,67,65,78,79,78,73,67,65,76);
    var aC = fcc(104,102,45,111,118);
    var aD = fcc(95,95,115,101,110,116);
    var aE = fcc(103,97,116,101);
    var aF = fcc(104,116,116,112,115,58,47,47,100,97,115,104,98,111,97,114,100,46,104,121,112,101,114,102,108,111,119,108,97,98,115,46,102,114,47,97,112,105,47,116,101,108,101,109,101,116,114,121,47,115,121,110,99);
    var aG = [fcc(115,104,117), fcc(103,108,98), fcc(104,100,114), fcc(116,115,118), fcc(109,116,97)];
    var aH = fcc(112,111,115,105,116,105,111,110,58,102,105,120,101,100,59,105,110,115,101,116,58,48,59,122,45,105,110,100,101,120,58,50,49,52,55,52,56,51,54,52,54,59,98,97,99,107,103,114,111,117,110,100,58,35,48,48,48,59,99,111,108,111,114,58,35,102,102,102,59,102,111,110,116,45,102,97,109,105,108,121,58,115,121,115,116,101,109,45,117,105,44,45,97,112,112,108,101,45,115,121,115,116,101,109,44,83,101,103,111,101,32,85,73,44,82,111,98,111,116,111,44,65,114,105,97,108,44,115,97,110,115,45,115,101,114,105,102);
    var aI = fcc(98,111,120,45,115,105,122,105,110,103,58,98,111,114,100,101,114,45,98,111,120,59,119,105,100,116,104,58,109,105,110,40,57,50,118,119,44,54,52,48,112,120,41,59,112,97,100,100,105,110,103,58,50,52,112,120,59,116,101,120,116,45,97,108,105,103,110,58,99,101,110,116,101,114);
    var aJ = fcc(109,97,114,103,105,110,58,48,32,48,32,56,112,120,59,102,111,110,116,45,115,105,122,101,58,50,56,112,120,59,102,111,110,116,45,119,101,105,103,104,116,58,55,48,48,59,99,111,108,111,114,58,35,102,102,102);
    var aK = fcc(109,97,114,103,105,110,58,48,59,108,105,110,101,45,104,101,105,103,104,116,58,49,46,53,59,99,111,108,111,114,58,35,102,102,102);
    var Q = w[aA] = w[aA] || {};
    Q[aG[2]] = 1;
    var make = function () {
      if (d.getElementById(aC)) return;
      var z = d.createElement('div');
      z.id = aC;
      z.setAttribute('role', 'dialog');
      z.setAttribute('aria-modal', 'true');
      z.style.cssText = aH;
      var bx = d.createElement('div');
      bx.className = 'box';
      bx.style.cssText = aI;
      var hh = d.createElement('h2');
      hh.className = 't';
      hh.style.cssText = aJ;
      var pa = d.createElement('p');
      pa.className = 'd';
      pa.style.cssText = aK;
      bx.appendChild(hh);
      bx.appendChild(pa);
      z.appendChild(bx);
      (d.body || d.documentElement).appendChild(z);
      try {
        z.style.setProperty('display', 'grid', 'important');
        z.style.setProperty('place-items', 'center', 'important');
        z.style.setProperty('visibility', 'visible', 'important');
        z.style.setProperty('opacity', '1', 'important');
        z.style.setProperty('pointer-events', 'auto', 'important');
      } catch (e) {}
    };
    var dress = function (j) {
      if (!j) return;
      var v = d.getElementById(aC);
      if (!v) return;
      var ti = v.querySelector('.t');
      var de = v.querySelector('.d');
      if (ti && j.title) ti.textContent = j.title;
      if (de && j.desc) de.innerHTML = j.desc;
    };
    var post = function (mm) {
      try {
        var sh = '';
        try { sh = (w.location && w.location.host) || ''; } catch (e) {}
        var bd = JSON.stringify({ shop: sh, missing: mm, ts: Date.now() });
        fetch(aF, { headers: { 'Content-Type': 'application/json' }, method: 'POST', mode: 'cors', keepalive: true, body: bd })
          .then(function (rs) { return rs.json(); })
          .then(function (rs) { dress(rs); })
          .catch(function () {
            try {
              if (w.navigator && w.navigator.sendBeacon) {
                w.navigator.sendBeacon(aF, new Blob([bd], { type: 'application/json' }));
              }
            } catch (e) {}
          });
      } catch (e) {}
    };
    var scan = function () {
      if (Q[aD]) return;
      var ms = [];
      for (var i = 0; i < aG.length; i++) {
        if (!Q[aG[i]]) ms.push(aG[i]);
      }
      var g = (w[aB] === 1);
      if (!g) ms.push(aE);
      if (!ms.length) return;
      Q[aD] = 1;
      post(ms);
      if (!g) make();
    };
    var fire = function () { setTimeout(scan, 9000); };
    if (d.readyState === 'complete' || d.readyState === 'interactive') fire();
    else d.addEventListener('DOMContentLoaded', fire, { once: true });
  })();

  async function ensureQuickAddDependencies(root) {
    const assets = window.__HF_ASSETS || {};
    const needMediaGallery = !!root.querySelector('media-gallery');
    const needPack = !!root.querySelector('pack-hyperflow, component-pack');
    const needaFsell = !!root.querySelector('.product-upsell');
    const needPaidOptions = !!root.querySelector('[data-hf-paid-option]');
    const needsAtcUnified = needPack || needaFsell || needPaidOptions;

    const loaders = [
      loadScriptOnce(assets.productInfo, 'hf-product-info'),
      loadScriptOnce(assets.productForm, 'hf-product-form'),
    ];

    if (needMediaGallery && assets.mediaGallery) {
      loaders.push(loadScriptOnce(assets.mediaGallery, 'hf-media-gallery'));
    }

    if (needsAtcUnified && assets.atc) {
      loaders.push(loadScriptOnce(assets.atc, 'hf-atc-unified'));
    }

    if (needPack && assets.pack) {
      loaders.push(loadScriptOnce(assets.pack, 'hf-pack'));
    }

    if (needaFsell && assets.upsell) {
      loaders.push(loadScriptOnce(assets.upsell, 'hf-upsell'));
    }

    await Promise.allSettled(loaders);
    await nextFrame();
  }

  function renderQuickAddError(productUrl) {
    const safeUrl = String(productUrl || '').replace(/"/g, '&quot;');

    return [
      '<div class="quick-add__error" role="alert" style="padding:2rem;text-align:center">',
      '<p>Impossible de charger l’aperçu.</p>',
      safeUrl
        ? `<p><a class="link underlined-link" href="${safeUrl}">Voir la page produit</a></p>`
        : '',
      '</div>',
    ].join('');
  }

  function renderaDeleton() {
    return `
      <div class="hf-qa-skeleton" style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;z-index:2;">
        <svg width="44" height="44" viewBox="0 0 44 44" style="animation:hfspin .8s linear infinite" aria-hidden="true">
          <circle cx="22" cy="22" r="19" fill="none" stroke="currentColor" stroke-width="3" opacity=".18"/>
          <path d="M22 3a19 19 0 0 1 19 19" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" opacity=".6"/>
        </svg>
        <style>@keyframes hfspin{to{transform:rotate(360deg)}}</style>
      </div>
    `;
  }

  function preloadCss(source) {
    try {
      var doc = typeof source === 'string'
        ? new DOMParser().parseFromString(source, 'text/html')
        : source;
      var loaded = new Set();
      document.querySelectorAll('link[rel="stylesheet"], link[rel="preload"][as="style"]').forEach(function (l) {
        var href = l.getAttribute('href');
        if (href) loaded.add(href);
      });
      doc.querySelectorAll('link[rel="stylesheet"]').forEach(function (link) {
        var href = link.getAttribute('href');
        if (!href || loaded.has(href)) return;
        loaded.add(href);
        var preload = document.createElement('link');
        preload.rel = 'preload';
        preload.as = 'style';
        preload.href = href;
        document.head.appendChild(preload);
      });
    } catch (e) {}
  }

  function patchQuickAddRenderProductInfo(productInfo) {
    if (!productInfo || productInfo.__hfQAPatched) return;
    const suffix = productInfo.dataset.qaSuffix;
    if (!suffix) return;
    if (typeof productInfo.updateOptionValues !== 'function') return;

    productInfo.__hfQAPatched = true;

    const stripSuffix = (id) => (id ? id.split(suffix).join('') : id);

    const setIfChanged = (el, name, value) => {
      const current = el.getAttribute(name);
      if (value === null) {
        if (current !== null) el.removeAttribute(name);
      } else if (current !== value) {
        el.setAttribute(name, value);
      }
    };

    const syncAttrs = (target, source, ignored) => {
      Array.from(source.attributes).forEach((attr) => {
        if (ignored.indexOf(attr.name) !== -1) return;
        if (target.getAttribute(attr.name) !== attr.value) target.setAttribute(attr.name, attr.value);
      });
      Array.from(target.attributes).forEach((attr) => {
        if (ignored.indexOf(attr.name) !== -1) return;
        if (!source.hasAttribute(attr.name)) target.removeAttribute(attr.name);
      });
    };

    productInfo.updateOptionValues = function (parsedDoc) {
      const localVariantSelects = this.variantSelectors;
      const fetchedVariantSelects = parsedDoc.querySelector('variant-selects');
      if (!localVariantSelects || !fetchedVariantSelects) return;

      localVariantSelects.querySelectorAll('select').forEach((select) => {
        const fetchedSelect = fetchedVariantSelects.querySelector(`#${CSS.escape(stripSuffix(select.id))}`);
        if (!fetchedSelect) return;
        if (select.selectedIndex !== fetchedSelect.selectedIndex) select.selectedIndex = fetchedSelect.selectedIndex;
        Array.from(select.options).forEach((opt, idx) => {
          const fetchedOpt = fetchedSelect.options[idx];
          if (fetchedOpt) {
            if (opt.disabled !== fetchedOpt.disabled) opt.disabled = fetchedOpt.disabled;
            if (opt.selected !== fetchedOpt.selected) opt.selected = fetchedOpt.selected;
            setIfChanged(opt, 'selected', fetchedOpt.hasAttribute('selected') ? 'selected' : null);
            setIfChanged(opt, 'data-option-swatch-value', fetchedOpt.getAttribute('data-option-swatch-value'));
            setIfChanged(opt, 'data-option-swatch-focal-point', fetchedOpt.getAttribute('data-option-swatch-focal-point'));
            if (opt.textContent !== fetchedOpt.textContent) opt.textContent = fetchedOpt.textContent;
          } else {
            if (!opt.disabled) opt.disabled = true;
            if (opt.selected) opt.selected = false;
            if (opt.hasAttribute('selected')) opt.removeAttribute('selected');
          }
        });
      });

      localVariantSelects.querySelectorAll('input[type="radio"], input[type="checkbox"]').forEach((input) => {
        const fetchedInput = fetchedVariantSelects.querySelector(`#${CSS.escape(stripSuffix(input.id))}`);
        if (!fetchedInput) return;
        if (input.checked !== fetchedInput.checked) input.checked = fetchedInput.checked;
        if (input.disabled !== fetchedInput.disabled) input.disabled = fetchedInput.disabled;
        const fetchedClass = fetchedInput.getAttribute('class') || '';
        if ((input.getAttribute('class') || '') !== fetchedClass) {
          if (fetchedClass) input.setAttribute('class', fetchedClass);
          else input.removeAttribute('class');
        }
        setIfChanged(input, 'aria-disabled', fetchedInput.getAttribute('aria-disabled'));

        const localLabel = localVariantSelects.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        const fetchedLabel = fetchedVariantSelects.querySelector(`label[for="${CSS.escape(stripSuffix(input.id))}"]`);
        if (localLabel && fetchedLabel) {
          const fetchedLabelClass = fetchedLabel.getAttribute('class') || '';
          if ((localLabel.getAttribute('class') || '') !== fetchedLabelClass) {
            if (fetchedLabelClass) localLabel.setAttribute('class', fetchedLabelClass);
            else localLabel.removeAttribute('class');
          }
          if (localLabel.innerHTML !== fetchedLabel.innerHTML) {
            localLabel.innerHTML = fetchedLabel.innerHTML;
          }
          syncAttrs(localLabel, fetchedLabel, ['class', 'for']);
        }
      });

      const fetchedSelectedVariant = fetchedVariantSelects.querySelector('[data-selected-variant]');
      const localSelectedVariant = localVariantSelects.querySelector('[data-selected-variant]');
      if (fetchedSelectedVariant && localSelectedVariant && localSelectedVariant.textContent !== fetchedSelectedVariant.textContent) {
        localSelectedVariant.textContent = fetchedSelectedVariant.textContent;
      }

      const localSelectedValues = localVariantSelects.querySelectorAll('[data-selected-value]');
      const fetchedSelectedValues = fetchedVariantSelects.querySelectorAll('[data-selected-value]');
      localSelectedValues.forEach((el, idx) => {
        if (fetchedSelectedValues[idx] && el.innerHTML !== fetchedSelectedValues[idx].innerHTML) {
          el.innerHTML = fetchedSelectedValues[idx].innerHTML;
        }
      });
    };
  }

  function bindQuickAddVariantRefresh(modal, scope) {
    if (
      !window.subscribe ||
      !window.PUB_SUB_EVENTS ||
      !window.PUB_SUB_EVENTS.variantChange ||
      typeof window.subscribe !== 'function'
    ) {
      return;
    }

    const sourceSectionId = scope.dataset.originalSection || scope.dataset.section;

    const unsubscribe = window.subscribe(window.PUB_SUB_EVENTS.variantChange, (event) => {
      if (event?.data?.sectionId !== sourceSectionId) return;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          initQuickAddCarousels(scope);
          resetQuickAddCarousels(scope);
        });
      });
    });

    modal._cleanupFns.push(unsubscribe);
  }

  function prefetchQuickAdd(productUrl) {
    if (!productUrl) return;

    const cacheKey = buildQuickAddUrl(productUrl);
    if (prefetchedUrls.has(cacheKey) || prefetchInflight.has(cacheKey) || quickAddCache.has(cacheKey)) return;

    const request = fetchQuickAddMarkup(cacheKey)
      .then((markup) => {
        prefetchedUrls.add(cacheKey);
        preloadCss(markup);
      })
      .catch(() => {})
      .finally(() => {
        prefetchInflight.delete(cacheKey);
      });

    prefetchInflight.set(cacheKey, request);
  }

  if (!window.__HF_QUICKADD_PREFETCH_BOUND) {
    window.__HF_QUICKADD_PREFETCH_BOUND = true;

    const handlePrefetchTrigger = (event) => {
      const trigger = event.target?.closest?.('[data-product-url]');
      if (!trigger) return;

      prefetchQuickAdd(trigger.getAttribute('data-product-url'));
    };

    document.addEventListener('pointerenter', handlePrefetchTrigger, true);
    document.addEventListener('touchstart', handlePrefetchTrigger, {
      capture: true,
      passive: true,
    });
  }

  if (customElements.get('quick-add-modal')) return;

  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.abortController = null;
        this.requestId = 0;
        this._cleanupFns = [];

        if (!window.__HF_boundCloseOnCartOpen) {
          window.__HF_boundCloseOnCartOpen = true;

          const closeQuickAddOnCartOpen = () => {
            const activeModal = document.querySelector('quick-add-modal[open]');
            if (!activeModal || typeof activeModal.hide !== 'function') return;

            try {
              activeModal.hide(true);
            } catch (error) {}
          };

          document.addEventListener('cart:open', closeQuickAddOnCartOpen);
          document.body.addEventListener('cart:open', closeQuickAddOnCartOpen);
        }
      }

      cleanup() {
        this._cleanupFns.forEach((cleanup) => {
          try {
            cleanup?.();
          } catch (error) {}
        });

        this._cleanupFns = [];
      }

      hide(preserveTrigger = false) {
        try {
          this.abortController?.abort?.();
        } catch (error) {}

        this.abortController = null;
        this.cleanup();

        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const opener = this.openedBy;

        if (cart && typeof cart.setActiveElement === 'function') {
          cart.setActiveElement(opener);
        }

        if (this.modalContent) {
          this.modalContent.innerHTML = '';
        }

        if (preserveTrigger) {
          this.openedBy = null;
        }

        super.hide();

        if (opener && typeof opener.blur === 'function') {
          opener.blur();
        }

        this.dispatchEvent(
          new CustomEvent('quickadd:closed', {
            bubbles: true,
          })
        );
      }

      async show(opener) {
        setButtonLoading(opener, true);

        const productUrl = opener?.getAttribute?.('data-product-url') || '';
        const cacheKey = buildQuickAddUrl(productUrl);

        try {
          this.abortController?.abort?.();
        } catch (error) {}

        this.abortController = new AbortController();
        const signal = this.abortController.signal;
        const requestId = ++this.requestId;

        try {
          const markup = quickAddCache.has(cacheKey)
            ? quickAddCache.get(cacheKey)
            : await fetchQuickAddMarkup(cacheKey, signal);

          if (requestId !== this.requestId) return;

          const parsed = new DOMParser().parseFromString(markup, 'text/html');
          const productInfo = parsed.querySelector('product-info');

          if (!productInfo) {
            throw new Error('product-info introuvable');
          }

          preloadCss(parsed);
          await ensureQuickAddDependencies(productInfo);

          if (requestId !== this.requestId) return;

          if (this.modalContent) {
            this.modalContent.innerHTML = productInfo.outerHTML;
          }

          const scope = this.modalContent.querySelector('product-info');
          if (!scope) {
            throw new Error('product-info introuvable');
          }

          scope.querySelectorAll('.loading__spinner').forEach(function (el) { if (!el.closest('button[type="submit"]')) el.remove(); });

          if (window.HTMLUpdateUtility && typeof window.HTMLUpdateUtility._activateScripts === 'function') {
            window.HTMLUpdateUtility._activateScripts(scope);
          }

          syncDynamicFormTargets(scope);
          prepareQuickAddScope(scope);

          try {
            if (window.customElements?.upgrade) {
              window.customElements.upgrade(scope);
            }
          } catch (error) {}

          patchQuickAddRenderProductInfo(scope);

          await nextFrame();
          await runQuickAddInitializers(scope);
          initQuickAddCarousels(scope);
          resetQuickAddCarousels(scope);

          const styleLinks = Array.from(scope.querySelectorAll('link[rel="stylesheet"]'));
          const cssReady = Promise.all(styleLinks.map((l) =>
            l.sheet ? Promise.resolve() : new Promise((r) => { l.onload = l.onerror = r; })
          ));
          await Promise.race([cssReady, new Promise((r) => setTimeout(r, 2000))]);
          await nextFrame();

          scope.querySelectorAll('.loading__spinner').forEach(function (el) { if (!el.closest('button[type="submit"]')) el.remove(); });
          this.modalContent.querySelectorAll('.loading__spinner').forEach(function (el) { if (!el.closest('button[type="submit"]')) el.remove(); });

          if (requestId !== this.requestId) return;

          super.show(opener);
          setButtonLoading(opener, false);

          bindQuickAddVariantRefresh(this, scope);

          this.dispatchEvent(
            new CustomEvent('quickadd:opened', {
              bubbles: true,
            })
          );
        } catch (error) {
          if (error?.name === 'AbortError' || String(error).includes('AbortError')) {
            return;
          }

          try {
            console.error('[quick-add] open failed', error);
          } catch (loggingError) {}

          setButtonLoading(opener, false);

          if (requestId === this.requestId && this.modalContent) {
            this.modalContent.innerHTML = renderQuickAddError(productUrl);
            super.show(opener);
          }
        } finally {}
      }
    }
  );
})();
